import { css } from '@emotion/react'
import { Cell, Grid, HFlow } from 'bold-ui'
import { Divider } from 'components/divider/Divider'
import { NumberField } from 'components/form'
import { EdemaRadioGroupField } from 'components/form/field/EdemaRadioGroupField'
import TipoGravidezSelectField, {
  TipoGravidezSelectFieldModel,
} from 'components/form/field/select/TipoGravidezSelectField'
import { SimNaoRadioGroupField } from 'components/form/field/SimNaoRadioGroupField'
import { InfoIcon } from 'components/InfoIcon'
import { useFlags } from 'config/useFlagsContext'
import { TipoGravidezEnum } from 'graphql/types.generated'
import { useAtendimentoContext } from 'hooks/atendimento-context/useAtendimentoContext'
import { useField } from 'react-final-form'
import { MetaPath } from 'util/metaPath'

import { PreNatalDumSection } from './components/dum-section/PreNatalDumSection'
import { FonteIdadeGestacionalSection } from './components/fonte-ig/FonteIdadeGestacionalSection'
import { IgDumPaper } from './components/IgDumPaper'
import { PreNatalBox } from './components/PreNatalBox'
import { RiscoGravidezMarker } from './components/RiscoGravidezMarker'
import { SdcRiscoPreEclampsiaSection } from './components/SdcRiscoPreEclampsiaSection'
import { PreNatalFormModel, TipoPreNatal } from './model-preNatal'

interface PreNatalFieldProps {
  name: MetaPath<PreNatalFormModel>
  tipoPreNatal: TipoPreNatal
  metaDum: MetaPath<string>
}

export function PreNatalField(props: PreNatalFieldProps) {
  const { name, tipoPreNatal, metaDum } = props

  const isContinuacaoPreNatal = tipoPreNatal === TipoPreNatal.CONTINUACAO_PRE_NATAL
  const { PILOTO_SDC_PRE_ECLAMPSIA_ENABLED } = useFlags()

  const {
    prontuario: { preNatalAtivo },
  } = useAtendimentoContext()

  const {
    input: { value: tipoGravidezField },
  } = useField<TipoGravidezSelectFieldModel>(name.tipoGravidez.absolutePath(), {
    subscription: { value: true },
  })

  const tipoGravidezValue = tipoGravidezField?.value ?? preNatalAtivo?.tipoGravidez?.tipoGravidez
  const isGravidezUnica = tipoGravidezValue === TipoGravidezEnum.UNICA

  const showGravidezPlanejada = !(isContinuacaoPreNatal && preNatalAtivo?.gravidezPlanejada !== null)

  const styles = createStyles()

  return (
    <PreNatalBox>
      <Grid gapVertical={0.5} gap={2}>
        <Cell size={4} lg={3}>
          <TipoGravidezSelectField name={name.tipoGravidez} initialValue={preNatalAtivo?.tipoGravidez} isPreNatal />
        </Cell>
        <Cell size={3} xs={4} sm={4} lg={3}>
          <NumberField name={name.alturaUterina} label='Altura uterina (cm)' maxLength={2} />
        </Cell>
        <Cell size={4}>
          <RiscoGravidezMarker isAltoRisco={preNatalAtivo?.altoRisco} />
        </Cell>

        <Cell>
          <EdemaRadioGroupField name={name.edema} />
        </Cell>
        <Cell>
          <SimNaoRadioGroupField name={name.movimentacaoFetal} label='Movimentação fetal' />
        </Cell>
        {showGravidezPlanejada && (
          <Cell>
            <SimNaoRadioGroupField name={name.gravidezPlanejada} label='Gravidez planejada' />
          </Cell>
        )}

        <Cell size={12}>
          <Grid>
            <Cell size={5} lg={3}>
              <NumberField name={name.batimentoCardiacoFetal} label='Batimento cardíaco fetal 1 (bpm)' maxLength={3} />
            </Cell>
            {!isGravidezUnica && (
              <Cell size={5} lg={3}>
                <NumberField
                  name={name.batimentoCardiacoFetal2}
                  label={
                    <HFlow hSpacing={0.5} alignItems='center'>
                      Batimento cardíaco fetal 2 (bpm)
                      <InfoIcon icon='infoCircleFilled' text='O prontuário realiza o controle de apenas duas BCFs.' />
                    </HFlow>
                  }
                  maxLength={3}
                />
              </Cell>
            )}
            {tipoPreNatal === TipoPreNatal.PRIMEIRO_ATENDIMENTO_PRE_NATAL && (
              <Cell>
                <IgDumPaper name={metaDum} />
              </Cell>
            )}
          </Grid>
        </Cell>

        {isContinuacaoPreNatal && (
          <Cell size={12} style={styles.firstDumRowCell}>
            <Divider style={styles.divider} />
            <PreNatalDumSection meta={metaDum} />
          </Cell>
        )}

        <Cell size={12}>
          <hr css={styles.topLine} />
          <FonteIdadeGestacionalSection persistedFonteIg={preNatalAtivo?.fonteIdadeGestacional} />
        </Cell>

        {PILOTO_SDC_PRE_ECLAMPSIA_ENABLED && (
          <Cell size={12}>
            <Divider style={styles.divider} />
            <SdcRiscoPreEclampsiaSection />
          </Cell>
        )}
      </Grid>
    </PreNatalBox>
  )
}

const createStyles = () => ({
  topLine: css`
    margin: 0.75rem 0 1.25rem 0;
  `,
  firstDumRowCell: css`
    padding-right: 0rem;
  `,
  divider: css`
    margin: 0.75rem 0 1.25rem 0;
  `,
})
